interface SoundDef {
  filename: string
  waitMs: number
}

const soundMap: Record<string, SoundDef> = {
  submit: { filename: 'decidemp3-14575.mp3', waitMs: 1300 },
  click: { filename: 'interface-124464.mp3', waitMs: 850 },
  middle: { filename: 'interface-124464.mp3', waitMs: 850 },
  cancel: { filename: 'level-up-3-199576.mp3', waitMs: 550 },
  owl: { filename: 'consult-owl-takes-off.mp3', waitMs: 2500 },
  // 羽根筆とスキル（#979。素材は Pixabay・doi 選定 2026-10-09）
  inspire: { filename: 'universfield-new-notification-036-485897.mp3', waitMs: 2400 }, // 写経でひらめいた
  learned: { filename: 'muzaproduction-positive-massive-logo-13624.mp3', waitMs: 6800 }, // 新しいスキルを覚えた
  writing: { filename: 'freesound_community-pencil-scratch-36190.mp3', waitMs: 1150 }, // 羽根筆が書いている間（くり返し鳴らす）
  reply: { filename: 'universfield-bubble-pop-02-293341.mp3', waitMs: 900 }, // 羽根筆・相談役が答えた
}

/**
 * SE の既定音量。BGM 側の {@link DEFAULT_MUSIC_VOLUME} (0.3 = -10.5 dB) と対称に、
 * SE を -6 dB (× 0.5) で流す。素材側は -14 LUFS に正規化してあり (#826)、
 * この掛け算で実効音量が BGM (-24.5 LUFS) より +4.5 dB 大きくなる
 * = UI 音として自然な前寄りに収まる。
 */
export const DEFAULT_SOUND_VOLUME = 0.5

const audioMap = new Map<string, HTMLAudioElement>()

export function useSound() {
  function playSound(name: string, options?: { wait?: boolean }): Promise<void> | void {
    if (import.meta.server) return
    const soundDef = soundMap[name]
    if (!soundDef) {
      console.warn(`Sound ${name} not found.`)
      return
    }

    const src = `/sounds/${soundDef.filename}`

    let audio = audioMap.get(src)
    if (!audio) {
      audio = new Audio(src)
      audioMap.set(src, audio)
    }

    const clone = audio.cloneNode(true) as HTMLAudioElement
    // volume は property なので cloneNode で複製されない。再生ごとに設定する。
    clone.volume = DEFAULT_SOUND_VOLUME
    clone.play()
      .catch((err) => {
        console.warn(`Failed to play sound ${name}`, err)
      })

    if (options?.wait) {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve()
        }, soundDef.waitMs)
      })
    }
  }

  /** くり返し鳴らし続ける（羽根筆が書いている間など）。返す関数で止める */
  function startLoop(name: string): () => void {
    if (import.meta.server) return () => {}
    const soundDef = soundMap[name]
    if (!soundDef) {
      console.warn(`Sound ${name} not found.`)
      return () => {}
    }
    const audio = new Audio(`/sounds/${soundDef.filename}`)
    audio.loop = true
    audio.volume = DEFAULT_SOUND_VOLUME
    audio.play().catch((err) => {
      console.warn(`Failed to play sound ${name}`, err)
    })
    return () => {
      audio.pause()
      audio.src = ''
    }
  }

  function preloadAll() {
    if (import.meta.server) return
    for (const def of Object.values(soundMap)) {
      const src = `/sounds/${def.filename}`
      if (!audioMap.has(src)) {
        const audio = new Audio(src)
        audio.preload = 'auto'
        audioMap.set(src, audio)
      }
    }
  }

  return {
    playSound,
    startLoop,
    preloadAll,
  }
}
